/**
 * Home.tsx — Painel client-first do Calm Operations Console.
 *
 * A entrada principal é uma lista calma de clientes/contas: sem CTA global,
 * sem dashboard KPI-first, e com ações ReadOnly/request separadas no contexto
 * da conta selecionada.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Shield } from '@phosphor-icons/react';
import { motion, useReducedMotion } from 'motion/react';

import { AccountActions } from '@/components/clients/AccountActions';
import { ClientLogo } from '@/components/clients/ClientLogo';
import { ClientRail } from '@/components/clients/ClientRail';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Container } from '@/components/ui/Container';
import { Eyebrow } from '@/components/ui/Eyebrow';
import { EnvironmentBadge } from '@/components/ui/EnvironmentBadge';
import { HomeToolbar, type ClientTypeFilter } from '@/components/ui/HomeToolbar';
import { PageHeader } from '@/components/ui/PageHeader';
import { Skeleton } from '@/components/ui/Skeleton';
import { ApiError, isApprover, jitApi } from '@/lib/api';
import type {
  AccountItem,
  ClientDetail,
  ClientSummary,
  EligibilityRule,
  RequestItem,
} from '@/lib/api';
import { useSession } from '@/lib/auth/useSession';
import { containerStagger, fadeUpItem, pickVariants } from '@/lib/motion/presets';
import { useFirstVisit } from '@/lib/motion/useFirstVisit';
import { usePinnedClients, useRecentClients } from '@/lib/storage/clientPreferences';
import { cn } from '@/lib/utils/cn';

export function Home() {
  const { user } = useSession();
  const firstName = user?.name?.split(' ')[0] ?? user?.username ?? 'time';

  // E3: motion control. animate so na primeira visita por sessao.
  const reduced = useReducedMotion() ?? false;
  const firstVisit = useFirstVisit('home');
  const animate = firstVisit && !reduced;
  // helper: container variants escolhidos por reduced-motion
  const initialState = animate ? 'hidden' : 'visible';

  const [requests, setRequests] = useState<RequestItem[] | null>(null);
  const [clients, setClients] = useState<ClientSummary[] | null>(null);
  const [eligibility, setEligibility] = useState<EligibilityRule[] | null>(null);
  const [pendingApprovals, setPendingApprovals] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // search + filter (E1)
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<ClientTypeFilter>('all');

  // E2: pinned + recents (localStorage)
  const pinned = usePinnedClients();
  const recents = useRecentClients();

  // expanded tile + detail cache
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  const [detailCache, setDetailCache] = useState<Record<string, ClientDetail>>({});
  const [detailLoading, setDetailLoading] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const userIsApprover = isApprover(user?.groups);

  useEffect(() => {
    const ctrl = new AbortController();

    Promise.all([
      jitApi.listMyRequests({ limit: 10 }, ctrl.signal),
      jitApi.getEligibility(ctrl.signal),
      jitApi.listClients(ctrl.signal),
    ])
      .then(([reqsRes, eligRes, clientsRes]) => {
        setRequests(reqsRes.items);
        setEligibility(eligRes.items);
        setClients(clientsRes.items);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 0) return;
        setError(err instanceof ApiError ? err.message : 'Falha ao carregar painel');
      });

    if (userIsApprover) {
      jitApi
        .listPendingApprovals({ limit: 100 }, ctrl.signal)
        .then((r) => setPendingApprovals(r.items.length))
        .catch(() => setPendingApprovals(0));
    }

    return () => ctrl.abort();
  }, [userIsApprover]);

  // Poll leve enquanto houver grant ativo: quando o ttl expira, o revoker
  // (server-side, a cada 1min) vira Granted->Expired. Sem esse refetch o card
  // "Acesso ativo" e o "Expira ..." ficam presos ate o usuario recarregar.
  const hasActiveGrant = (requests ?? []).some((i) => i.status === 'Granted');
  useEffect(() => {
    if (!hasActiveGrant) return;
    const id = window.setInterval(() => {
      jitApi
        .listMyRequests({ limit: 10 })
        .then((r) => setRequests(r.items))
        .catch(() => {});
    }, 20_000);
    return () => window.clearInterval(id);
  }, [hasActiveGrant]);

  // accounts elegiveis pra Home (so clientes onde user tem eligibility)
  const eligibleAccountIds = useMemo(
    () => new Set(eligibility?.map((r) => r.account_id) ?? []),
    [eligibility],
  );

  // Modelo aberto: todos os usuarios veem todos os clientes. Acesso é
  // concedido via aprovacao (eligibility é visual hint, nao gate aqui).
  const allClients = useMemo(() => clients ?? [], [clients]);

  // E1: search (nome + slug + account_ids cacheados) + type filter
  const visibleClients = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allClients.filter((c) => {
      if (typeFilter !== 'all' && c.type !== typeFilter) return false;
      if (!q) return true;
      if (c.name.toLowerCase().includes(q)) return true;
      if (c.slug.toLowerCase().includes(q)) return true;
      // accounts conhecidas via detail cache (clientes já expandidos)
      const cached = detailCache[c.slug];
      if (cached?.accounts?.some((a) => a.account_id.includes(q))) return true;
      return false;
    });
  }, [allClients, query, typeFilter, detailCache]);

  const hasFilters = query.trim().length > 0 || typeFilter !== 'all';

  function clearFilters() {
    setQuery('');
    setTypeFilter('all');
  }

  function handleToggle(slug: string) {
    if (expandedSlug === slug) {
      setExpandedSlug(null);
      return;
    }
    setExpandedSlug(slug);
    recents.add(slug);
    setDetailError(null);
    if (!detailCache[slug]) {
      setDetailLoading(slug);
      jitApi
        .getClient(slug)
        .then((d) => {
          setDetailCache((prev) => ({ ...prev, [slug]: d }));
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 0) return;
          setDetailError(err instanceof ApiError ? err.message : 'Falha ao carregar contas');
        })
        .finally(() => setDetailLoading(null));
    }
  }

  // E2: resolver clientes pinned/recents (map slug -> ClientSummary)
  const clientMap = useMemo(() => {
    const m = new Map<string, ClientSummary>();
    for (const c of allClients) m.set(c.slug, c);
    return m;
  }, [allClients]);

  const pinnedClients = useMemo(
    () => pinned.items.map((slug) => clientMap.get(slug)).filter((c): c is ClientSummary => !!c),
    [pinned.items, clientMap],
  );
  const pinnedSet = useMemo(() => new Set(pinned.items), [pinned.items]);

  const recentClients = useMemo(
    () =>
      recents.items
        .filter((slug) => !pinnedSet.has(slug))
        .map((slug) => clientMap.get(slug))
        .filter((c): c is ClientSummary => !!c),
    [recents.items, pinnedSet, clientMap],
  );

  function handleSelectFromRail(slug: string) {
    setExpandedSlug(slug);
    recents.add(slug);
    if (!detailCache[slug]) {
      setDetailLoading(slug);
      jitApi
        .getClient(slug)
        .then((d) => setDetailCache((prev) => ({ ...prev, [slug]: d })))
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 0) return;
          setDetailError(err instanceof ApiError ? err.message : 'Falha ao carregar contas');
        })
        .finally(() => setDetailLoading(null));
    }
    // scroll smooth pro grid principal
    requestAnimationFrame(() => {
      const el = document.getElementById(`client-tile-panel-${slug}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  function handleTogglePin(slug: string) {
    pinned.toggle(slug);
  }

  const activeCount = requests?.filter((i) => i.status === 'Granted').length ?? 0;
  const pendingCount = requests?.filter((i) => i.status === 'PendingApproval').length ?? 0;
  const activeRequest = requests?.find((i) => i.status === 'Granted' && i.ttl_iso);

  return (
    <>
      <PageHeader
        eyebrow="Acesso por cliente"
        title={`Olá, ${firstName}`}
        description="Escolha um cliente para abrir o contexto de contas e solicitar acesso elevado quando precisar."
      />

      <Container size="lg" className="py-8">
        {/* Pendentes (TEAMAdmin only) */}
        {userIsApprover && pendingApprovals !== null && pendingApprovals > 0 && (
          <Link
            to="/approvals"
            className={cn(
              'mb-6 flex items-center justify-between gap-3 rounded-md border border-border bg-surface',
              'px-3.5 py-2.5 text-[0.8125rem] transition-colors hover:border-ink/20 hover:bg-surface-muted/60',
            )}
          >
            <span className="flex items-center gap-2.5">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-degraded" />
              <span className="text-ink">
                <strong className="font-semibold tabular-nums">{pendingApprovals}</strong>{' '}
                {pendingApprovals === 1
                  ? 'solicitação aguarda sua aprovação'
                  : 'solicitações aguardam sua aprovação'}
              </span>
            </span>
            <ArrowRight size={14} weight="bold" className="text-muted" aria-hidden="true" />
          </Link>
        )}

        {error && (
          <div className="mb-6 rounded-md border border-error/30 bg-error/5 px-3 py-2 text-sm text-critical">
            {error}
          </div>
        )}

        {/* Entrada client-first */}
        <section className="mb-8" aria-labelledby="home-client-entry-title">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <Eyebrow>Entrada de acesso</Eyebrow>
              <h2 id="home-client-entry-title" className="mt-1 text-xl font-semibold tracking-tight text-ink">
                Clientes disponíveis
              </h2>
            </div>
            <span className="hidden font-mono text-[0.625rem] uppercase tracking-[0.16em] text-muted sm:block">
              Busque por cliente ou conta
            </span>
          </div>

          {clients === null || eligibility === null ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-28" />
              ))}
            </div>
          ) : allClients.length === 0 ? (
            <Card padding="lg">
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <Shield size={40} weight="duotone" className="text-muted" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-ink">Nenhum cliente cadastrado</h3>
                <p className="max-w-md text-sm text-muted">
                  Quando um admin cadastrar clientes, eles aparecem aqui.
                </p>
              </div>
            </Card>
          ) : (
            <>
              <HomeToolbar
                query={query}
                onQueryChange={setQuery}
                typeFilter={typeFilter}
                onTypeFilterChange={setTypeFilter}
                visibleCount={visibleClients.length}
                totalCount={allClients.length}
              />

              {/* E2: rails so aparecem se nao ha filtros ativos (evita ruido) */}
              {!hasFilters && (
                <>
                  <ClientRail
                    variant="pinned"
                    clients={pinnedClients}
                    onSelect={handleSelectFromRail}
                    onUnpin={(slug) => pinned.remove(slug)}
                  />
                  <ClientRail
                    variant="recents"
                    clients={recentClients}
                    onSelect={handleSelectFromRail}
                  />
                </>
              )}

              {visibleClients.length === 0 ? (
                <Card padding="lg">
                  <div className="flex flex-col items-center gap-3 py-8 text-center">
                    <p className="font-mono text-[0.6875rem] uppercase tracking-[0.14em] text-muted">
                      Sem resultados
                    </p>
                    <h3 className="text-base font-semibold text-ink">Nada bate com os filtros</h3>
                    <p className="max-w-md text-[0.8125rem] text-muted">
                      Ajuste a busca ou o filtro de tipo pra encontrar o que procura.
                    </p>
                    {hasFilters && (
                      <Button size="sm" variant="secondary" onClick={clearFilters}>
                        Limpar filtros
                      </Button>
                    )}
                  </div>
                </Card>
              ) : (
                <Card padding="none" className="overflow-hidden">
                  <div className="border-b border-border bg-surface px-4 py-4">
                    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
                      <div>
                        <p className="font-mono text-[0.625rem] uppercase tracking-[0.16em] text-muted">
                          Lista de clientes
                        </p>
                        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted">
                          Selecione um cliente para abrir contas, console ReadOnly e solicitação elevada.
                        </p>
                      </div>
                      <OperationalSummary
                        requestsLoaded={requests !== null}
                        activeCount={activeCount}
                        pendingCount={pendingCount}
                        recentCount={requests?.length ?? 0}
                        activeExpiry={activeRequest?.ttl_iso ?? undefined}
                      />
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3 border-t border-border/70 pt-3">
                      <span className="text-[0.8125rem] text-muted">
                        <strong className="font-semibold tabular-nums text-ink">{visibleClients.length}</strong>{' '}
                        {visibleClients.length === 1 ? 'cliente visível' : 'clientes visíveis'}
                      </span>
                      <span className="hidden font-mono text-[0.625rem] uppercase tracking-[0.14em] text-muted sm:inline">
                        Clique para expandir
                      </span>
                    </div>
                  </div>

                  <motion.ul
                    className="divide-y divide-border"
                    variants={pickVariants(reduced, containerStagger)}
                    initial={initialState}
                    animate="visible"
                    aria-label="Clientes e contas"
                  >
                    {visibleClients.map((c) => {
                      const expanded = expandedSlug === c.slug;
                      return (
                        <motion.li key={c.slug} variants={pickVariants(reduced, fadeUpItem)}>
                          <ClientRow
                            client={c}
                            expanded={expanded}
                            pinned={pinnedSet.has(c.slug)}
                            onToggle={() => handleToggle(c.slug)}
                            onTogglePin={() => handleTogglePin(c.slug)}
                          />
                          {expanded && (
                            <div
                              id={`client-tile-panel-${c.slug}`}
                              className="border-t border-border bg-[#f7f4ef]/70 px-5 py-5"
                              role="region"
                              aria-label={`Contas de ${c.name}`}
                            >
                              <ClientExpansion
                                clientDetail={detailCache[c.slug] ?? null}
                                loading={detailLoading === c.slug}
                                error={detailError}
                                eligibleAccountIds={eligibleAccountIds}
                              />
                            </div>
                          )}
                        </motion.li>
                      );
                    })}
                  </motion.ul>
                </Card>
              )}
            </>
          )}
        </section>

      </Container>
    </>
  );
}

const CLIENT_STATUS_DOT: Record<ClientSummary['status'], string> = {
  active: 'bg-healthy',
  paused: 'bg-degraded',
  offboarded: 'bg-muted/40',
};

interface ClientRowProps {
  client: ClientSummary;
  expanded: boolean;
  pinned: boolean;
  onToggle: () => void;
  onTogglePin: () => void;
}

function ClientRow({ client, expanded, pinned, onToggle, onTogglePin }: ClientRowProps) {
  const eligibleCount = client.eligible_account_ids.length;
  const totalCount = client.account_count;

  return (
    <div
      className={cn(
        'grid gap-3 px-4 py-3 transition-colors sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center',
        expanded ? 'bg-signal/5' : 'bg-surface hover:bg-surface-muted/45',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`client-tile-panel-${client.slug}`}
        aria-label={client.name}
        className="group flex min-w-0 items-center gap-3 rounded-md text-left focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-signal"
      >
        <ClientLogo
          name={client.name}
          logoUrl={client.logo_url}
          sizeClassName="h-10 w-10 rounded-md"
          textClassName="font-mono text-xs"
          className="bg-surface"
        />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate text-[0.9375rem] font-semibold text-ink">{client.name}</span>
            {client.type === 'internal' && (
              <span className="rounded border border-border bg-surface px-1.5 py-0.5 font-mono text-[0.58rem] uppercase tracking-[0.12em] text-muted">
                Interno
              </span>
            )}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.75rem] text-muted">
            <span className="font-mono">@{client.slug}</span>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', CLIENT_STATUS_DOT[client.status])} />
              <span className="font-mono uppercase tracking-[0.12em]">{client.status}</span>
            </span>
            <span className="font-mono tabular-nums">
              {totalCount} {totalCount === 1 ? 'conta' : 'contas'}
              {eligibleCount > 0 ? ` · ${eligibleCount} elegíveis` : ''}
            </span>
          </span>
        </span>
      </button>

      <div className="flex items-center justify-between gap-2 sm:justify-end">
        {eligibleCount > 0 ? (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-healthy/20 bg-healthy/10 px-2 py-1 font-mono text-[0.625rem] uppercase tracking-[0.12em] text-healthy">
            <CheckCircle size={12} weight="fill" aria-hidden="true" />
            Elegível
          </span>
        ) : (
          <span className="rounded-md border border-border bg-surface px-2 py-1 font-mono text-[0.625rem] uppercase tracking-[0.12em] text-muted">
            Requer aprovação
          </span>
        )}
        <button
          type="button"
          onClick={onTogglePin}
          aria-pressed={pinned}
          aria-label={pinned ? `Desfixar ${client.name}` : `Fixar ${client.name}`}
          className={cn(
            'rounded-md border px-2.5 py-1 font-mono text-[0.625rem] uppercase tracking-[0.12em] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal',
            pinned
              ? 'border-signal/30 bg-signal/10 text-signal'
              : 'border-border bg-surface text-muted hover:border-ink/20 hover:text-ink',
          )}
        >
          {pinned ? 'Fixado' : 'Fixar'}
        </button>
      </div>
    </div>
  );
}

interface ClientExpansionProps {
  clientDetail: ClientDetail | null;
  loading: boolean;
  error: string | null;
  eligibleAccountIds: Set<string>;
}

function ClientExpansion({
  clientDetail,
  loading,
  error,
  eligibleAccountIds,
}: ClientExpansionProps) {
  if (error) {
    return (
      <div
        role="alert"
        className="rounded-md border border-error/30 bg-error/5 px-3 py-2 text-[0.8125rem] text-critical"
      >
        {error}
      </div>
    );
  }
  if (loading || !clientDetail) {
    return (
      <div className="rounded-2xl border border-border bg-surface/80 p-4 shadow-soft">
        <div className="mb-3 flex items-center justify-between gap-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-20" />
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      </div>
    );
  }
  const accounts: AccountItem[] = clientDetail.accounts ?? [];
  if (accounts.length === 0) {
    return (
      <p className="rounded-2xl border border-border bg-surface/80 px-4 py-4 text-[0.8125rem] text-muted shadow-soft">
        Esse cliente ainda não tem contas cadastradas.
      </p>
    );
  }
  return (
    <div className="rounded-xl border border-border bg-surface/90 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3.5">
        <div>
          <p className="font-mono text-[0.625rem] uppercase tracking-[0.16em] text-muted">
            Contas disponíveis
          </p>
          <p className="mt-1 text-sm leading-relaxed text-muted">
            Escolha a conta certa antes de solicitar acesso elevado.
          </p>
        </div>
        <span className="font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-muted">
          {accounts.length} {accounts.length === 1 ? 'conta' : 'contas'}
        </span>
      </div>
      <ul className="divide-y divide-border" aria-label={`Contas de ${clientDetail.client.name}`}>
        {accounts.map((acc) => (
          <AccountRow
            key={acc.account_id}
            account={acc}
            clientSlug={clientDetail.client.slug}
            isEligible={eligibleAccountIds.has(acc.account_id)}
          />
        ))}
      </ul>
    </div>
  );
}

interface AccountRowProps {
  account: AccountItem;
  clientSlug: string;
  isEligible: boolean;
}

function AccountRow({ account, clientSlug, isEligible }: AccountRowProps) {
  return (
    <li className="grid gap-3 px-4 py-3.5 transition-colors hover:bg-surface-muted/35 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-[0.9rem] font-semibold leading-tight text-ink">{account.name}</p>
          <EnvironmentBadge environment={account.environment} />
          {isEligible ? (
            <span className="inline-flex items-center gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em] text-healthy">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-healthy" />
              elegível
            </span>
          ) : (
            <span className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-muted">
              requer aprovação
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.75rem] text-muted">
          <span className="font-mono tabular-nums">{account.account_id}</span>
          {account.notes ? <span className="line-clamp-1">{account.notes}</span> : null}
        </div>
      </div>
      <AccountActions
        account={account}
        clientSlug={clientSlug}
        density="compact"
        className="justify-end lg:pl-4"
      />
    </li>
  );
}

interface OperationalSummaryProps {
  requestsLoaded: boolean;
  activeCount: number;
  pendingCount: number;
  recentCount: number;
  activeExpiry?: string;
}

function OperationalSummary({
  requestsLoaded,
  activeCount,
  pendingCount,
  recentCount,
  activeExpiry,
}: OperationalSummaryProps) {
  const items = [
    {
      label: 'Ativo',
      value: activeCount,
      hint: activeExpiry ? `até ${formatExpiry(activeExpiry)}` : activeCount === 0 ? 'nenhum' : 'agora',
      tone: activeCount > 0 ? 'bg-healthy' : 'bg-muted/35',
    },
    {
      label: 'Pendente',
      value: pendingCount,
      hint: pendingCount > 0 ? 'em análise' : 'limpo',
      tone: pendingCount > 0 ? 'bg-degraded' : 'bg-muted/35',
    },
    {
      label: 'Recentes',
      value: recentCount,
      hint: '7 dias',
      tone: 'bg-muted/35',
    },
  ];

  return (
    <div
      aria-label="Resumo operacional"
      className="grid min-w-[min(100%,28rem)] grid-cols-3 border-y border-border bg-transparent"
    >
      {items.map((item) => (
        <div key={item.label} className="border-l border-border/70 px-3 py-2 first:border-l-0">
          <div className="flex items-center gap-1.5">
            <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', item.tone)} />
            <p className="truncate font-mono text-[0.6rem] uppercase tracking-[0.14em] text-muted">
              {item.label}
            </p>
          </div>
          {requestsLoaded ? (
            <p className="mt-1 font-mono text-base font-semibold leading-none tabular-nums text-ink">
              {item.value}
            </p>
          ) : (
            <Skeleton className="mt-1 h-5 w-7" />
          )}
          <p className="mt-0.5 truncate text-[0.66rem] text-muted">{item.hint}</p>
        </div>
      ))}
    </div>
  );
}

function formatExpiry(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
